// Custom hook for quiz timer functionality with persistence
import { useState, useEffect, useCallback, useRef } from "react";

interface TimerState {
  expectedEndTime: number; // When the quiz should end
  initialTime: number; // Total duration in seconds
  isRunning: boolean;
}

export const useQuizTimer = (
  initialTime: number,
  onTimeUp?: () => void,
  quizId?: string,
  attemptId?: string
) => {
  const storageKey = quizId ? `quiz-timer-${quizId}` : null;

  // Load timer state from localStorage
  const loadTimerState = useCallback((): TimerState | null => {
    if (!storageKey) return null;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate the stored data
        if (
          parsed.expectedEndTime &&
          typeof parsed.expectedEndTime === "number"
        ) {
          console.log(
            "📖 Loading timer state from localStorage:",
            storageKey,
            parsed
          );
          return parsed;
        }
      }
    } catch (error) {
      console.error("Error loading timer state:", error);
    }
    console.log(
      "❌ No valid timer state found in localStorage for:",
      storageKey
    );
    return null;
  }, [storageKey]);

  // Save timer state to localStorage - only called on start and addTime
  const saveTimerState = useCallback(
    (state: TimerState) => {
      if (storageKey) {
        try {
          console.log("Saving timer state:", storageKey, state);
          localStorage.setItem(storageKey, JSON.stringify(state));
        } catch (error) {
          console.error("Error saving timer state:", error);
        }
      }
    },
    [storageKey]
  );

  // Clear timer state from localStorage
  const clearTimerState = useCallback(() => {
    if (storageKey) {
      try {
        console.log("Clearing timer state:", storageKey);
        localStorage.removeItem(storageKey);
        console.log(
          "After clearing, value is:",
          localStorage.getItem(storageKey)
        );
      } catch (error) {
        console.error("Error clearing timer state:", error);
      }
    }
  }, [storageKey]);

  const startTimeRef = useRef<number>(0);

  // Initialize state from localStorage
  const [isRunning, setIsRunning] = useState(() => {
    const stored = loadTimerState();
    if (stored && stored.expectedEndTime > Date.now()) {
      startTimeRef.current = stored.expectedEndTime;
      return stored.isRunning;
    }
    return false;
  });

  const [timeRemaining, setTimeRemaining] = useState(() => {
    const stored = loadTimerState();
    if (stored && stored.expectedEndTime > Date.now()) {
      const remaining = Math.max(
        0,
        Math.floor((stored.expectedEndTime - Date.now()) / 1000)
      );
      return remaining;
    }
    return initialTime;
  });
  // Calculate current remaining time based on expected end time
  const getCurrentTimeRemaining = useCallback(() => {
    if (!isRunning || startTimeRef.current === 0) {
      const stored = loadTimerState();
      return stored
        ? Math.max(0, Math.floor((stored.expectedEndTime - Date.now()) / 1000))
        : initialTime;
    }

    const remaining = Math.max(
      0,
      Math.floor((startTimeRef.current - Date.now()) / 1000)
    );
    return remaining;
  }, [isRunning, initialTime, loadTimerState]);

  useEffect(() => {
    if (isRunning) {
      const interval = setInterval(() => {
        const currentRemaining = getCurrentTimeRemaining();
        setTimeRemaining(currentRemaining);

        if (currentRemaining <= 0) {
          console.log(
            "⏰ TIMER EXPIRED! Calling onTimeUp and clearing timer..."
          );
          setIsRunning(false);
          // Clear timer state immediately when it expires
          clearTimerState();
          onTimeUp?.();
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isRunning, getCurrentTimeRemaining, onTimeUp, clearTimerState]);

  const start = useCallback(
    (newTime?: number) => {
      // Check if we already have a valid timer in storage - don't overwrite it
      const existingTimer = loadTimerState();
      if (existingTimer && existingTimer.expectedEndTime > Date.now()) {
        // Valid timer already exists, just resume it
        startTimeRef.current = existingTimer.expectedEndTime;
        setIsRunning(true);
        const remaining = Math.max(
          0,
          Math.floor((existingTimer.expectedEndTime - Date.now()) / 1000)
        );
        setTimeRemaining(remaining);
        return; // Don't save again, use existing timer
      }

      // No valid timer exists, create a new one
      const timeToUse = newTime ?? initialTime;
      const expectedEndTime = Date.now() + timeToUse * 1000;
      startTimeRef.current = expectedEndTime;
      setIsRunning(true);
      setTimeRemaining(timeToUse);

      // Only save to localStorage when creating a NEW timer
      saveTimerState({
        expectedEndTime,
        initialTime: timeToUse,
        isRunning: true,
      });
    },
    [initialTime, saveTimerState, loadTimerState]
  );

  const stop = useCallback(() => {
    console.log("🛑 Stopping timer and clearing localStorage for:", storageKey);
    setIsRunning(false);
    startTimeRef.current = 0;
    clearTimerState();
  }, [clearTimerState, storageKey]);

  const reset = useCallback(
    (newTime?: number) => {
      const resetTime = newTime ?? initialTime;
      setTimeRemaining(resetTime);
      setIsRunning(false);
      startTimeRef.current = 0;
      clearTimerState();
    },
    [initialTime, clearTimerState]
  );

  const addTime = useCallback(
    (seconds: number) => {
      // For adding time, extend the expected end time
      const newExpectedEndTime = startTimeRef.current + seconds * 1000;
      startTimeRef.current = newExpectedEndTime; // Update the ref as well

      saveTimerState({
        expectedEndTime: newExpectedEndTime,
        initialTime, // Keep the original initial time
        isRunning,
      });
    },
    [initialTime, isRunning, saveTimerState]
  );

  const formatTime = useCallback((seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const getTimeStatus = useCallback(() => {
    const percentage = (timeRemaining / initialTime) * 100;
    if (percentage <= 10) return "critical";
    if (percentage <= 25) return "warning";
    return "normal";
  }, [timeRemaining, initialTime]);

  return {
    timeRemaining,
    formattedTime: formatTime(timeRemaining),
    isRunning,
    start,
    stop,
    reset,
    addTime,
    isTimeUp: timeRemaining === 0,
    timeStatus: getTimeStatus(),
    percentageRemaining: (timeRemaining / initialTime) * 100,
  };
};
